import { CompanyType } from 'constants/company';
import {
  GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP,
  GlobalSupplierListAccessType,
  GlobalSupplierListPermissionGroupType
} from 'constants/list';
import { PermissionGrantScopeType, PERMISSION_GRANT_SCOPE } from 'constants/product';

//
// --- Types ---

export interface GlobalSupplierListType {
  accessKey: GlobalSupplierListAccessType;
  classifications: ReadonlyArray<any>;
  /** a timestamp in ms */
  creationTime: number;
  creatorCompanyKey: number;
  description: string | null;
  listKey: number;
  listName: string;
  listType: number;
  miListAssignments: ReadonlyArray<GlobalSupplierListAssignmentType>;
  ownerCompany: GlobalSupplierListOwnerCompanyType;
  ownerPerson: any;
  updatedByUserKey: number;
  /** a timestamp in ms */
  updateTime: number;
}

export interface GlobalSupplierListRequestType
  extends Omit<
    GlobalSupplierListType,
    'creationTime' | 'listKey' | 'miListAssignments' | 'ownerCompany' | 'updateTime'
  > {
  listKey?: number;
  miListAssignments: ReadonlyArray<any>;
  ownerCompany: GlobalSupplierListOwnerCompanyRequestType;
}

export interface GlobalSupplierListAssignmentType {
  listAssignmentKey: number;
  miList: null; // this value always appears to be null
  numericKey1: number | null;
  numericKey2: number | null; // this value always appears to be null
  numericKey3: number | null; // this value always appears to be null
  stringKey1: string | null; // this value always appears to be null
  stringKey2: string | null; // this value always appears to be null
  stringKey3: string | null; // this value always appears to be null
}

export interface GlobalSupplierListAssignmentRequestType extends Partial<GlobalSupplierListAssignmentType> {
  listAssignmentKey?: number;
  numericKey1: number | null;
}

export interface GlobalSupplierListMasterCompanyType {
  companyKey: number;
  companyName: string;
  companyStatusCode: string;
  companyType: string;
}

/** has a more elaborate masterCompany type than logged in users company type */
export interface GlobalSupplierListOwnerCompanyType extends CompanyType {
  masterCompany: GlobalSupplierListMasterCompanyType;
}

export interface GlobalSupplierListOwnerCompanyRequestType {
  companyKey: number;
}

interface PrepareGlobalSupplierListForRequestOptions {
  isNewList?: boolean;
}

export type GlobalSupplierListPermissionGrantScopeMapType = Partial<Record<PermissionGrantScopeType, boolean>>;

//
// --- Functions ---

/**
 * Determine if the entity represented by a tree node is on a global supplier list
 */
export function isSupplierTreeNodeOnGlobalSupplierList(
  treeNode: any,
  globalSupplierListCompanyKeys: ReadonlyArray<number>
) {
  if (!treeNode || typeof treeNode !== 'object' || !Array.isArray(globalSupplierListCompanyKeys)) {
    return false;
  }

  if (treeNode.eipCompanyKey) {
    // note: eipCompanyKey on nodes is type `number` most often, but can periodically be type `string`
    return globalSupplierListCompanyKeys.includes(parseInt(treeNode.eipCompanyKey, 10));
  }

  return false;
}

/**
 * Determine if a tree node on a global supplier list result can be added to a supplier list.
 */
export function canAddTreeNodeToGlobalSupplierList(treeNode: any, isIPAFB = false): boolean {
  if (!treeNode || typeof treeNode !== 'object') {
    return false;
  }
  if (isIPAFB && treeNode?.name) {
    // IPAFB nodes can always be added to a global supplier list
    return true;
  }

  return !!treeNode.eipCompanyKey;
}

/**
 * Modifies global supplier list objects so it can be converted to xml and remove properties that the API is not
 * expecting
 */
export function prepareGlobalSupplierListForRequest(
  list: GlobalSupplierListType,
  opts: PrepareGlobalSupplierListForRequestOptions = {}
) {
  const { isNewList = false } = opts;

  if (!list) {
    return;
  }

  const miListAssignments: ReadonlyArray<GlobalSupplierListAssignmentRequestType> = Array.isArray(
    list.miListAssignments
  )
    ? list.miListAssignments.map((assignment: GlobalSupplierListAssignmentType) => {
        const newAssignment: GlobalSupplierListAssignmentRequestType = {
          numericKey1: assignment.numericKey1
        };

        return assignment.listAssignmentKey
          ? { ...newAssignment, listAssignmentKey: assignment.listAssignmentKey }
          : newAssignment;
      })
    : [];

  const { creationTime, updateTime, ...listWithoutTimestamps } = list;

  const globalSupplierListForRequest: GlobalSupplierListRequestType = {
    ...listWithoutTimestamps,
    miListAssignments: miListAssignments,
    ownerCompany: { companyKey: list.ownerCompany && list.ownerCompany.companyKey ? list.ownerCompany.companyKey : 0 }
  };

  if (isNewList) {
    delete globalSupplierListForRequest.listKey;
  }

  return globalSupplierListForRequest;
}

//
// ---  Global Supplier List Permission Util Functions ---

/**
 * Calculate the appropriate supplier list permission group for an individual grant
 * permission (ex: create, edit, etc). An empty array represents no permissions.
 *
 * This conversion changes an object based structure to a simple array of strings.
 *
 * Examples:
 *   - given a map object `{ 1000: true }`, it returns ['all'],
 *   - given a map object `{ 1001: true, 1002: true }`, it returns ['company owner', 'owner']
 */
export function convertGrantScopeMapToPermissionGroups(
  grantScopeMap: GlobalSupplierListPermissionGrantScopeMapType
): ReadonlyArray<GlobalSupplierListPermissionGroupType> {
  if (!grantScopeMap || typeof grantScopeMap !== 'object') {
    return [];
  }

  // if all, just return an array with that value. This mimics EIP permission interface
  // which will unselect all other scopes when "all" is selected
  if (grantScopeMap[PERMISSION_GRANT_SCOPE.ALL]) {
    return [GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.ALL];
  }

  // return array of remaining scopes if set, but return an empty array if none to simulate no permissions
  return [
    grantScopeMap[PERMISSION_GRANT_SCOPE.COMPANY_OWNER] ? GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.COMPANY_OWNER : '',
    grantScopeMap[PERMISSION_GRANT_SCOPE.OWNER] ? GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.OWNER : ''
  ].filter((group): group is GlobalSupplierListPermissionGroupType => !!group);
}

/**
 * Determine if a person (the logged in person typically) can take an action on a global supplier list.
 *
 * There are 4 options where this is allowed:
 *   1. Permissions allow action for all lists
 *   2. Permissions allow action for company owner, so verify company keys match
 *   3. Permissions allow action for owner only, so verify person key is the same as the list owner
 *      person key
 *   4. No permissions are set, so do not allow permission for anyone
 *
 * Notes:
 * - The screen in EIP where these permissions are set will clear out all other options when "All" is checked,
 *   so if all is set, no other permissions will be provided
 * - Both owner and owner company can be set on their own, or both can be checked.
 * - It is also possible, no scope items are checked, representing permission is not allowed for anyone
 */

export function areAllAllowedByPermissionGroups(
  permissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>
) {
  return Array.isArray(permissionGroups) && permissionGroups.includes(GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.ALL);
}

export function isListActionAllowedByPermissionGroups(
  permissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>,
  globalSupplierList: GlobalSupplierListType,
  personKey: number,
  personCompanyKey: number
): boolean {
  if (!Array.isArray(permissionGroups) || !permissionGroups.length) {
    return false;
  }

  if (areAllAllowedByPermissionGroups(permissionGroups)) {
    return true;
  }

  if (!globalSupplierList || typeof globalSupplierList !== 'object') {
    return false;
  }

  if (
    permissionGroups.includes(GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.COMPANY_OWNER) &&
    globalSupplierList.ownerCompany &&
    globalSupplierList.ownerCompany.companyKey === personCompanyKey
  ) {
    return true;
  }

  if (
    permissionGroups.includes(GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.OWNER) &&
    globalSupplierList.ownerPerson &&
    globalSupplierList.ownerPerson.id === personKey
  ) {
    return true;
  }

  return false;
}
